/**
 * Bottom sheet with three options for joining a call:
 * 1. Join with Video (camera + mic)
 * 2. Join with Audio (mic only)
 * 3. Join with A/V Off (listen only)
 */

import React, { useCallback, useRef, forwardRef, useImperativeHandle } from 'react'
import { StyleSheet, View, useWindowDimensions } from 'react-native'
import { BottomSheetBackdrop, BottomSheetModal, BottomSheetView } from '@gorhom/bottom-sheet'
import { isNumber } from 'lodash'
import { DS } from '../../design-system'
import { isWeb, useMaxWidth } from '../WebWrapper'
import type { JoinMode } from '../../hooks/av'

export type JoinCallSheetRef = {
  open: () => void
  close: () => void
}

type Props = {
  onJoin: (mode: JoinMode) => void
  onDismiss?: () => void
}

export const JoinCallSheet = forwardRef<JoinCallSheetRef, Props>(({ onJoin, onDismiss }, ref) => {
  const bottomSheetRef = useRef<BottomSheetModal>(null)
  const { width: screenWidth } = useWindowDimensions()
  const maxWidth = useMaxWidth()
  const _maxWidth = isNumber(maxWidth) ? maxWidth : screenWidth
  const marginHorizontal = screenWidth > _maxWidth ? (screenWidth - _maxWidth) / 2 : 0

  useImperativeHandle(ref, () => ({
    open: () => bottomSheetRef.current?.present(),
    close: () => bottomSheetRef.current?.dismiss()
  }))

  const handleJoin = useCallback(
    (mode: JoinMode) => {
      bottomSheetRef.current?.dismiss()
      onJoin(mode)
    },
    [onJoin]
  )

  const renderBackdrop = useCallback(
    (props: React.ComponentProps<typeof BottomSheetBackdrop>) => (
      <BottomSheetBackdrop {...props} disappearsOnIndex={-1} appearsOnIndex={0} opacity={0.5} />
    ),
    []
  )

  return (
    <BottomSheetModal
      ref={bottomSheetRef}
      enableDynamicSizing
      backgroundStyle={styles.background}
      handleIndicatorStyle={styles.handle}
      backdropComponent={renderBackdrop}
      onDismiss={onDismiss}
      containerStyle={{
        marginLeft: !isWeb && marginHorizontal > 0 ? marginHorizontal : undefined
      }}
      style={{
        marginHorizontal: 'auto',
        maxWidth: maxWidth
      }}
    >
      <BottomSheetView style={styles.content}>
        <DS.Text variant="Heading 3/Regular" textAlign="center">
          Join Call
        </DS.Text>
        <DS.Text variant="Body Large/Regular" textAlign="center">
          Turn on your camera to connect with others and elevate the game.
        </DS.Text>
        <View style={styles.buttons}>
          <DS.Button variant="Medium/Primary" type="On Light" onPress={() => handleJoin('video')}>
            {"Yes, let's go live"}
          </DS.Button>
          <DS.Button variant="Medium/Primary" type="On Dark" style={styles.secondaryButton} onPress={() => handleJoin('audio')}>
            Turn on audio only
          </DS.Button>
          <DS.Button variant="Medium/Primary" type="On Dark" style={styles.secondaryButton} onPress={() => handleJoin('off')}>
            No, keep camera and mic off
          </DS.Button>
        </View>
      </BottomSheetView>
    </BottomSheetModal>
  )
})

JoinCallSheet.displayName = 'JoinCallSheet'

const styles = StyleSheet.create({
  background: {
    backgroundColor: DS.palette['Brand/Cream']
  },
  handle: {
    backgroundColor: '#0003'
  },
  content: {
    paddingHorizontal: DS.spacing['16'],
    paddingTop: DS.spacing['8'],
    paddingBottom: DS.spacing['32'],
    gap: DS.spacing['16'],
    alignItems: 'center'
  },
  buttons: {
    width: '100%',
    gap: 8
  },
  secondaryButton: {
    backgroundColor: DS.palette['Brand/Cream'],
    borderWidth: 1,
    borderColor: DS.palette['Brand/Light Grey']
  }
})
